import { createFileRoute, notFound } from "@tanstack/react-router";
import { getDocPageFn } from "@/server/docs";
import { DocPage } from "@/components/docs/DocPage";
import { siteUrl } from "@/config/site";
import { docsTitle } from "@/config/docs";
import { DEFAULT_LOCALE } from "@/lib/i18n";
import { articleSocialCardMeta } from "@/lib/social-card-meta";
import {
  definedTermSetJsonLd,
  docPageJsonLd,
  faqPageJsonLd,
  jsonLdScripts,
} from "@/lib/jsonld";
import { katexLink, ogImageMeta } from "@/lib/seo";
import { docsDocumentTitle, localizedDocsName } from "@/lib/docs-seo";
import { readLocale } from "@/lib/route-head";

export const Route = createFileRoute("/($locale)/docs/$")({
  loader: async ({ params }) => {
    const slug = params._splat ?? "";
    const locale = (params as { locale?: string }).locale ?? DEFAULT_LOCALE;
    const [doc, docsName] = await Promise.all([
      getDocPageFn({ data: { slug, locale } }),
      localizedDocsName(locale),
    ]);
    if (!doc) throw notFound();
    return { ...doc, docsName };
  },
  head: ({ loaderData, params }) => {
    const meta = loaderData?.meta;
    // docsName is for the breadcrumb crumb / JSON-LD only; `<title>` uses
    // docsDocumentTitle (w1/m140). English docsTitle only if the key is missing.
    const docsName = loaderData?.docsName ?? docsTitle;
    if (!meta) return { meta: [{ title: docsDocumentTitle(docsName) }] };
    const canonicalUrl = `${siteUrl}/docs/${meta.id}`;
    return {
      meta: [
        { title: docsDocumentTitle(meta.title) },
        { name: "description", content: meta.description },
        ...(meta.keywords.length > 0
          ? [{ name: "keywords", content: meta.keywords.join(", ") }]
          : []),
        // Title-specific card through the existing generator (hazardous
        // titles fall back to the site card inside `ogImageUrl`), plus the
        // shared page-owned card fields over this locale's own meta.
        ...ogImageMeta(meta.title),
        ...articleSocialCardMeta({
          locale: readLocale(params),
          title: meta.title,
          description: meta.description,
          canonicalUrl,
        }),
      ],
      scripts: jsonLdScripts(
        ...docPageJsonLd({
          title: meta.title,
          description: meta.description,
          keywords: meta.keywords,
          url: canonicalUrl,
          homeUrl: siteUrl,
          docsName,
          docsUrl: `${siteUrl}/docs`,
          authors: loaderData?.authorLinks,
          authorBaseUrl: `${siteUrl}/blog/authors`,
          dateModified: loaderData?.lastUpdated,
        }),
        // Only the glossary page carries terms (see getDoc); empty elsewhere.
        ...definedTermSetJsonLd({
          url: canonicalUrl,
          name: meta.title,
          description: meta.description,
          terms: loaderData?.glossaryTerms ?? [],
        }),
        // Likewise, only the FAQ page carries Q&A entries.
        ...(loaderData?.faqEntries?.length
          ? [faqPageJsonLd(loaderData.faqEntries)]
          : []),
      ),
      links: [
        { rel: "canonical", href: canonicalUrl },
        // KaTeX CSS only when this doc contains server-rendered math.
        ...(loaderData?.hasMath ? [katexLink] : []),
      ],
    };
  },
  component: function DocRoute() {
    const doc = Route.useLoaderData();
    return <DocPage {...doc} />;
  },
});
